if (!customElements.get('button-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.button-list__items',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
      this.classes = {
        grid: 'f-flex',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.slides = this.sliderWrapper.querySelectorAll('.swiper-slide');

      this.enableSlider = this.dataset.enableSlider === 'true';

      this.sliderInstance = false;

      if (this.enableSlider) {
        this.init();
        document.addEventListener('matchMobile', () => {
          this.init();
        });
        document.addEventListener('unmatchMobile', () => {
          this.init();
        });
      }
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap');
      const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10 || 12;
      const sliderOptions = {
        slidesPerView: 'auto',
        centeredSlides: false,
        spaceBetween: spaceBetween,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);
        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        this.sliderInstance.slider.on('progress', ({ progress }) => {
          this.updateSliderReach(progress === 0 ? 'begin' : progress === 1 ? 'end' : 'progress');
        });
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideTo(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }

    updateSliderReach = (position) => {
      this.dataset.sliderReach = position;
    };
  }
  customElements.define('button-list', CollectionList);
}

if (!customElements.get('remote-tab-bar')) {
  class RemoteTabBar extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabNav = this.querySelector('.tabs__nav-js');
      if (!this.tablist) return;

      this.tabs = [...this.tablist.querySelectorAll('[role="tab"]')];
      if (!this.tabs.length) return;

      this.selectedIndex = 0;
      this.onTabClick = this.onTabClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', this.onTabClick);
        tab.dataset.tabIndex = String(i);
      });
      this.tablist.addEventListener('keydown', this.onKeyDown);

      this.syncAccessibleNames();
      const initial = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      this.selectTab(initial >= 0 ? initial : 0, false);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
      this.tablist?.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }

    onSectionLoad() {
      if (!this.isConnected) return;
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.syncAccessibleNames();
        const idx = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        this.selectTab(idx >= 0 ? idx : 0, false);
      });
    }

    syncAccessibleNames() {
      this.tabs.forEach((tab) => {
        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId && document.getElementById(panelId);
        if (panel && tab.id) {
          panel.setAttribute('aria-labelledby', tab.id);
        }
      });
    }

    getPanelIds() {
      return this.tabs.map((t) => t.getAttribute('aria-controls')).filter(Boolean);
    }

    getPanelById(id) {
      return id ? document.getElementById(id) : null;
    }

    onTabClick(evt) {
      evt.preventDefault();
      const tab = evt.currentTarget;
      const index = this.tabs.indexOf(tab);
      if (index === -1) return;
      this.selectTab(index);
    }

    selectTab(tabIndex, animated = true) {
      const tab = this.tabs[tabIndex];
      if (!tab) return;

      const panelId = tab.getAttribute('aria-controls');
      const toPanel = this.getPanelById(panelId);
      if (!toPanel) return;

      const fromPanel = this.tabs[this.selectedIndex]
        ? this.getPanelById(this.tabs[this.selectedIndex].getAttribute('aria-controls'))
        : null;

      if (tabIndex === this.selectedIndex && toPanel && !toPanel.hidden) {
        return;
      }

      this.tabs.forEach((t, i) => t.setAttribute('aria-selected', i === tabIndex));

      this.selectedIndex = tabIndex;

      const applyVisibility = () => {
        this.getPanelIds().forEach((id) => {
          const el = this.getPanelById(id);
          if (el) el.hidden = id !== panelId;
        });
        toPanel.querySelector('grid-list')?.showGridItems?.();
      };

      const motion = window.FoxTheme?.Motion;
      const reduceMotion = window.FoxTheme?.config?.motionReduced;

      if (
        animated === false ||
        !fromPanel ||
        fromPanel === toPanel ||
        !motion ||
        reduceMotion
      ) {
        applyVisibility();
        this.scrollToActiveTab(tab);
        this.dispatchTabChange(tabIndex, toPanel);
        return;
      }

      motion
        .animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: 0.3 })
        .finished.then(() => {
          if (!this.isConnected) return;
          fromPanel.hidden = true;
          this.getPanelIds().forEach((id) => {
            const el = this.getPanelById(id);
            if (el) el.hidden = id !== panelId;
          });
          toPanel.hidden = false;
          motion
            .animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.3 })
            .finished.then(() => {
              if (!this.isConnected) return;
              toPanel.querySelector('grid-list')?.showGridItems?.();
            });
        });

      this.scrollToActiveTab(tab);
      this.dispatchTabChange(tabIndex, toPanel);
    }

    dispatchTabChange(index, panel) {
      this.dispatchEvent(
        new CustomEvent('tabChange', {
          bubbles: true,
          detail: { selectedIndex: index, selectedTab: panel },
        })
      );
    }

    scrollToActiveTab(toButton) {
      if (!this.tabNav) return;
      const containerGap = 15;
      const scrollRect = toButton.getBoundingClientRect();
      const boxRect = this.tabNav.getBoundingClientRect();
      const scrollLeft = this.tabNav.scrollLeft;
      const scrollOffset = scrollRect.x + scrollLeft - boxRect.x - containerGap;
      this.tabNav.scrollTo({
        left: scrollOffset,
        behavior: 'smooth',
      });
    }

    onKeyDown(e) {
      if (e.keyCode !== 39 && e.keyCode !== 37) return;
      e.preventDefault();
      let next = this.selectedIndex;
      if (e.keyCode === 39) {
        next = next + 1 >= this.tabs.length ? 0 : next + 1;
      } else {
        next = next - 1 < 0 ? this.tabs.length - 1 : next - 1;
      }
      this.tabs[next].focus();
      this.selectTab(next);
    }
  }

  customElements.define('remote-tab-bar', RemoteTabBar);
}
